import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Loader2, Upload, X } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export interface InternationalShippingImportItem {
  id: string;
  product_description?: string;
  manufacturer?: string;
  origin_country?: string;
  hs_code?: string;
  confidence?: string;
}

interface InternationalShippingImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
  onConfirm: (items: InternationalShippingImportItem[]) => void;
}

const parseImportJson = (text: string): { items: InternationalShippingImportItem[]; error: string | null } => {
  const trimmed = text.trim();
  if (!trimmed) return { items: [], error: "Paste a JSON array of product updates." };

  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      return { items: [], error: "JSON must be an array." };
    }

    const items: InternationalShippingImportItem[] = [];
    for (let index = 0; index < parsed.length; index++) {
      const value = parsed[index];
      if (!value || typeof value !== "object") continue;

      const id = typeof (value as any).id === "string" ? (value as any).id.trim() : "";
      if (!id) continue;

      items.push({
        id,
        product_description:
          typeof (value as any).product_description === "string" ? (value as any).product_description : undefined,
        manufacturer: typeof (value as any).manufacturer === "string" ? (value as any).manufacturer : undefined,
        origin_country: typeof (value as any).origin_country === "string" ? (value as any).origin_country : undefined,
        hs_code: typeof (value as any).hs_code === "string" ? (value as any).hs_code : undefined,
        confidence: typeof (value as any).confidence === "string" ? (value as any).confidence : undefined,
      });
    }

    if (items.length === 0) {
      return { items: [], error: "No valid items found (each entry must have a non-empty string `id`)." };
    }

    return { items, error: null };
  } catch (error) {
    return { items: [], error: error instanceof Error ? error.message : "Invalid JSON." };
  }
};

export function InternationalShippingImportModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}: InternationalShippingImportModalProps) {
  const [jsonText, setJsonText] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setJsonText("");
      setError("");
    }
  }, [isOpen]);

  const parsed = useMemo(() => parseImportJson(jsonText), [jsonText]);

  const stats = useMemo(() => {
    const total = parsed.items.length;
    const withDescription = parsed.items.filter((i) => typeof i.product_description === "string" && i.product_description.trim()).length;
    const withManufacturer = parsed.items.filter((i) => typeof i.manufacturer === "string" && i.manufacturer.trim()).length;
    const withOrigin = parsed.items.filter((i) => typeof i.origin_country === "string" && i.origin_country.trim()).length;
    const withHsCode = parsed.items.filter((i) => typeof i.hs_code === "string" && i.hs_code.trim()).length;
    return { total, withDescription, withManufacturer, withOrigin, withHsCode };
  }, [parsed.items]);

  const validateAndSubmit = () => {
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError("");
    onConfirm(parsed.items);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{ margin: 0, padding: "1rem", top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-500 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Globe className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 }}
                      className="text-xl font-bold text-white"
                    >
                      Import Intl Shipping & Customs
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-white/80 text-sm"
                    >
                      Paste a JSON array to bulk update products by id
                    </motion.p>
                  </div>
                </div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={loading}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-gray-50/50 to-white space-y-4">
              <div className="text-sm text-gray-700">
                Fields supported per item: <span className="font-medium">id</span>,{" "}
                <span className="font-medium">product_description</span>, <span className="font-medium">manufacturer</span>,{" "}
                <span className="font-medium">origin_country</span>, <span className="font-medium">hs_code</span>.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Items</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.total}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Description</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.withDescription}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Manufacturer</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.withManufacturer}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="text-xs text-gray-500">Origin</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.withOrigin}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3">
                  <div className="text-xs text-gray-500">HS Code</div>
                  <div className="text-lg font-semibold text-gray-900">{stats.withHsCode}</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="intl-import-json" className="text-sm font-medium text-gray-700">
                  JSON list
                </Label>
                <Textarea
                  id="intl-import-json"
                  value={jsonText}
                  onChange={(e) => {
                    setJsonText(e.target.value);
                    if (error) setError("");
                  }}
                  disabled={loading}
                  placeholder='[{"id":"...","product_description":"...","manufacturer":"...","origin_country":"JM","hs_code":"220210","confidence":"high"}]'
                  className={`min-h-[260px] font-mono text-xs ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                />
                {(error || parsed.error) && (
                  <div className="text-sm text-red-600">
                    {error || parsed.error}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="text-xs text-gray-500">
                  Updates only set fields that are present and non-empty in the JSON.
                </div>
                <div className="flex items-center justify-end gap-4">
                  <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="px-6 h-11 rounded-xl font-medium">
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={validateAndSubmit}
                    disabled={loading}
                    className="px-6 h-11 rounded-xl font-medium bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Apply Updates
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

